import { useState } from "react";
import Header from "@/components/Header";
import { 
  HelpCircle, MessageCircle, Mail, Phone, ChevronDown, 
  ChevronUp, ExternalLink, FileText, AlertTriangle,
  Send, Loader2
} from "lucide-react";
import { cn } from "@/utils/cn";

const faqs = [
  {
    question: "How do I go online to receive calls?",
    answer: "Use the Online/Offline toggle in the sidebar. When you're online, users can see you and request instant calls. Make sure you're in a quiet environment with stable internet before going online."
  },
  {
    question: "How do scheduled consultations work?",
    answer: "Users can book appointments during your available hours (set in Schedule > Availability). You'll receive a notification before the scheduled time. The user will initiate the call at the scheduled time."
  },
  {
    question: "When do I get paid?",
    answer: "Earnings are calculated based on your per-minute rate. Payouts are processed on the 1st and 15th of each month. Minimum payout threshold is ₹500. Make sure your bank details are updated in your Profile."
  },
  {
    question: "What if a call gets disconnected?",
    answer: "If a call is disconnected due to technical issues, the user can re-initiate the call. You'll only be paid for the minutes you were actually connected. If there's a dispute, contact support."
  },
  {
    question: "How can I improve my visibility?",
    answer: "Complete your profile with a good photo and detailed description. Maintain a high rating by providing quality consultations. Stay online during peak hours (typically 6-10 PM). Respond promptly to chat messages."
  },
  {
    question: "Can I see client contact details?",
    answer: "For privacy and security, client contact details (phone, email) are not visible. All communication happens through the platform. This protects both you and the clients."
  },
  {
    question: "How do I report a problematic client?",
    answer: "If you encounter inappropriate behavior, use the 'Report Issue' form below or contact support directly. We take such reports seriously and may take action against the user's account."
  }
];

const FAQItem = ({ faq, isOpen, onToggle }) => (
  <div className="border-b">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
    >
      <span className="font-medium text-gray-900 pr-4">{faq.question}</span>
      {isOpen ? (
        <ChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0" />
      ) : (
        <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
      )}
    </button>
    {isOpen && (
      <div className="px-4 pb-4 text-sm text-gray-600">
        {faq.answer}
      </div>
    )}
  </div>
);

const ContactCard = ({ icon: Icon, title, value, action, actionLabel }) => (
  <div className="bg-gray-50 rounded-lg p-4">
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center flex-shrink-0">
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1">
        <h4 className="font-medium text-gray-900">{title}</h4>
        <p className="text-sm text-gray-500">{value}</p>
        {action && (
          <a
            href={action}
            className="inline-flex items-center gap-1 text-sm text-orange-500 hover:text-orange-600 mt-1"
          >
            {actionLabel}
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    </div>
  </div>
);

export default function SupportPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const [reportForm, setReportForm] = useState({
    type: "technical",
    subject: "",
    description: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmitReport = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    // TODO: API call to submit support ticket
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setSubmitting(false);
    setSubmitted(true);
    setReportForm({ type: "technical", subject: "", description: "" });
  };

  return (
    <>
      <Header name="Help & Support" />
      <div className="w-full p-4 max-w-4xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4">
            {/* FAQs */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-4 border-b">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 text-orange-500" />
                  Frequently Asked Questions
                </h2>
              </div>
              <div>
                {faqs.map((faq, index) => (
                  <FAQItem
                    key={index}
                    faq={faq}
                    isOpen={openFaq === index}
                    onToggle={() => setOpenFaq(openFaq === index ? null : index)}
                  />
                ))}
              </div>
            </div>

            {/* Report Issue Form */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-4 border-b">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  Report an Issue
                </h2>
              </div>
              <div className="p-4">
                {submitted ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-3">
                      <Send className="h-6 w-6" />
                    </div>
                    <h3 className="font-medium text-gray-900">Report Submitted</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      We'll get back to you within 24 hours
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="mt-4 text-sm text-orange-500 hover:text-orange-600"
                    >
                      Submit another report
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmitReport} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Issue Type
                      </label>
                      <select
                        value={reportForm.type}
                        onChange={(e) => setReportForm({ ...reportForm, type: e.target.value })}
                        className="w-full p-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="technical">Technical Issue</option>
                        <option value="payment">Payment Issue</option>
                        <option value="client">Client Complaint</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Subject
                      </label>
                      <input
                        type="text"
                        value={reportForm.subject}
                        onChange={(e) => setReportForm({ ...reportForm, subject: e.target.value })}
                        placeholder="Brief description of the issue"
                        required
                        className="w-full p-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={reportForm.description}
                        onChange={(e) => setReportForm({ ...reportForm, description: e.target.value })}
                        placeholder="Provide details about the issue..."
                        required
                        rows={4}
                        className="w-full p-2 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" />
                          Submit Report
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>

          {/* Contact Info Sidebar */}
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Contact Us</h3>
              <div className="space-y-3">
                <ContactCard
                  icon={Mail}
                  title="Email Support"
                  value="support@apnasanatan.com"
                  action="mailto:support@apnasanatan.com"
                  actionLabel="Send email"
                />
                <ContactCard
                  icon={Phone}
                  title="Phone Support"
                  value="+91 98765 43210"
                  action="tel:+919876543210"
                  actionLabel="Call now"
                />
                <ContactCard
                  icon={MessageCircle}
                  title="WhatsApp"
                  value="Quick responses"
                  action="https://wa.me/919876543210"
                  actionLabel="Chat on WhatsApp"
                />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Resources</h3>
              <div className="space-y-2">
                <a href="#" className="flex items-center gap-2 text-sm text-gray-600 hover:text-orange-500">
                  <FileText className="h-4 w-4" />
                  Partner Guidelines
                </a>
                <a href="#" className="flex items-center gap-2 text-sm text-gray-600 hover:text-orange-500">
                  <FileText className="h-4 w-4" />
                  Terms of Service
                </a>
                <a href="#" className="flex items-center gap-2 text-sm text-gray-600 hover:text-orange-500">
                  <FileText className="h-4 w-4" />
                  Privacy Policy
                </a>
              </div>
            </div>

            <div className="bg-orange-50 rounded-lg p-4">
              <p className="text-sm text-orange-700">
                <strong>Support Hours:</strong><br />
                Monday - Saturday<br />
                9:00 AM - 9:00 PM IST
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
